import { useOrganization, useOrganizationList } from '@clerk/react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { SnackbarProvider } from 'notistack';
import { lazy, Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuth, useIsMember } from 'src/auth';
import NavigationBlocker from 'src/components/containers/NavigationBlocker';
import { useAiV2Enabled } from 'src/hooks/useAiV2Enabled';
import { ChatScrollMemoryProvider } from 'src/lib/ai/chatScrollMemory';
import { useChatIdentity } from 'src/lib/ai/useChatIdentity';
import { buildAppHref } from 'src/lib/routing';
import AiChatDrawer from 'src/modules/ai/components/AiChatDrawer';
import { useAiInboxOnlyMode } from 'src/modules/ai-inbox/hooks/useAiInboxOnlyMode';
import SiteHeader, { GLOBAL_APP_BAR_HEIGHT } from 'src/modules/pageFrame/SiteHeader';
import StaleBundleNotice from 'src/modules/pageFrame/StaleBundleNotice';
import EditingStateProvider from 'src/providers/EditingStateProvider';
import GridFocusProvider from 'src/providers/GridFocusProvider';
import { PageWorkAtRiskProvider } from 'src/providers/PageWorkAtRiskProvider';
import SidebarContextProvider from 'src/providers/SidebarContextProvider';
import { appLinks } from 'src/routing/appLinks';
import { isChatHostPath } from 'src/routing/chatHostPaths';
import SideNavigation from './SideNavigation';

const AiChatLauncher = lazy(() => import('src/modules/chat').then((module) => ({ default: module.AiChatLauncher })));

export default function AgrifulFrame({ children }: React.PropsWithChildren<{}>) {
  const { setActive, userMemberships, isLoaded: isOrgListLoaded } = useOrganizationList({ userMemberships: true });
  const { organization } = useOrganization();
  const { currentUser } = useAuth();
  const aiV2 = useAiV2Enabled();
  const isMember = useIsMember();
  const stockChat = aiV2 && isMember;
  const chatIdentity = useChatIdentity();
  const { locked, isAllowedPath } = useAiInboxOnlyMode();

  const router = useRouter();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });

  useEffect(() => {
    if (!currentUser) return;
    if (organization) return;
    if (!isOrgListLoaded) return;

    if (userMemberships?.data?.length > 0) {
      setActive({ organization: userMemberships.data[0].organization.id });
      return;
    }

    if (pathname === buildAppHref(router, appLinks.notAMember())) return;

    void navigate(appLinks.notAMember());
  }, [setActive, userMemberships, isOrgListLoaded, pathname, organization, currentUser, navigate, router]);

  // AI Inbox-only lockdown: when the `ai-inbox-only` flag is on for this org,
  // bounce the user back to /ai-inbox if they land anywhere outside the
  // allow list (set by useAiInboxOnlyMode — /ai-inbox + /user-profile).
  const shouldRedirectForLockdown = locked && !isAllowedPath(pathname);
  useEffect(() => {
    if (!shouldRedirectForLockdown) return;
    void navigate(appLinks.aiInbox());
  }, [shouldRedirectForLockdown, navigate]);

  // While the redirect is in flight (one tick), render nothing so the frame
  // chrome (header, sidebar, LandingPage welcome cell, etc.) doesn't flash.
  if (shouldRedirectForLockdown) return null;

  const content = (
    <EditingStateProvider scopeKey={stockChat && isChatHostPath(pathname) ? 'ai-v2-chat' : undefined}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <SnackbarProvider maxSnack={5} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
          <NavigationBlocker />
          <StaleBundleNotice />
          <SidebarContextProvider>
            <SiteHeader />
            <SideNavigation />
            {!stockChat && <AiChatDrawer />}

            <GridFocusProvider>
              <Box
                component="main"
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  pt: `${GLOBAL_APP_BAR_HEIGHT}px`,
                  minHeight: '100vh',
                  flexDirection: 'column',
                }}
              >
                {children}
              </Box>
            </GridFocusProvider>
          </SidebarContextProvider>
        </SnackbarProvider>
      </Box>
    </EditingStateProvider>
  );
  return (
    <ChatScrollMemoryProvider identityKey={chatIdentity} enabled={Boolean(stockChat)}>
      <PageWorkAtRiskProvider enabled={Boolean(stockChat)}>
        {content}
        {stockChat && (
          <ErrorBoundary fallback={null}>
            <Suspense fallback={null}>
              <AiChatLauncher />
            </Suspense>
          </ErrorBoundary>
        )}
      </PageWorkAtRiskProvider>
    </ChatScrollMemoryProvider>
  );
}
